import { useCallback } from 'react';
import { type IntlShape, useIntl } from 'react-intl';

import { useSharedStateSelector } from '@trello/shared-state';

import {
  type Facet,
  type FeatureNames,
  FeedbackFeatures,
} from './featuresInfo';
import {
  feedbackCollectorSharedState,
  type OnFeedbackGiven,
} from './feedbackCollectorSharedState';

interface UseFeedbackCollectorProps {
  featureName: FeatureNames;
  onFeedbackGiven?: OnFeedbackGiven;
  /**
   * Extra per-instance context merged into the feedback events and the Jira
   * submission's context field, alongside the feature's own static
   * attributes (e.g. which category of a multi-category feature this
   * feedback is about).
   */
  attributes?: Record<string, string>;
}

interface GetAnalyticsAttributesOptions {
  numNegativeFacets?: number;
  fullyPositive?: boolean;
  fullyNegative?: boolean;
  numInBatch?: number;
}

interface ShowFeedbackCollectorOptions {
  traceId: string;
  flagTreatment: boolean;
  /**
   * Context only known at the moment the collector is shown, merged over the
   * hook's own `attributes`.
   */
  attributes?: Record<string, string>;
}

interface UseFeedbackCollectorReturn {
  getAnalyticsAttributes: (
    options?: GetAnalyticsAttributesOptions,
  ) => Record<string, number | string>;
  negativeFacets: Facet[];
  getFeedbackCollectorProjectKey: () => string;
  isOpen: boolean;
  traceId?: string;
  showFeedbackCollector: (options: ShowFeedbackCollectorOptions) => void;
  dismissFeedbackCollector: () => void;
}

/**
 * Calculates a sentiment modifier based on the ratio of observed negative
 * facets to the total number of negative facets defined for a feature.
 *
 * When no negative facets are observed (i.e. `numNegativeFacets` is `0` or
 * `undefined`), the modifier defaults to `1` (wholly positive sentiment).
 * Otherwise it returns a value in the range `[-1, 0)` representing the
 * proportion of negative facets observed, negated — where `-1` means every
 * defined negative facet was observed. If the feature defines no negative
 * facets, a denominator of `1` is used to avoid division by zero.
 *
 * @param featureName - The name of the feature whose negative-facet
 *   definitions are used as the denominator of the ratio.
 * @param intl - An internationalization object used to retrieve negative facets from the feature.
 * @param numNegativeFacets - The number of negative facets observed in the
 *   current interaction. When omitted or `0`, the function treats the
 *   sentiment as fully positive.
 * @returns A sentiment modifier: `1` for positive sentiment, or a negative
 *   value representing the proportion of negative facets observed.
 *
 * @example
 * // Feature with 5 defined negative facets, 2 observed → -0.4
 * getSentimentModifier('smartScheduling', 2);
 *
 * @example
 * // No negative facets observed → 1
 * getSentimentModifier('smartScheduling');
 */
const getSentimentModifier = (
  featureName: FeatureNames,
  intl: IntlShape,
  numNegativeFacets?: number,
): number => {
  //count up the number of negative facets for the feature, and return a negative modifier for the number of negative facets
  //observed in this way

  if (!numNegativeFacets) {
    return 1; //default - when there are no negative facets observed, we assume wholly positive sentiment
  }

  const feature = FeedbackFeatures[featureName];
  const negativeFacets = feature.negativeFacets(intl);
  const featureNegativeFacetsCount =
    negativeFacets.length === 0 ? 1 : negativeFacets.length;
  return ((numNegativeFacets ?? 1) / featureNegativeFacetsCount) * -1;
};

/**
 * The `useFeedbackCollector` hook allows clients to control showing a feedback collector for an AI feature
 * programmatically, as well as retrieving the associated analytics metadata for that feature.
 * @param featureName The feature name for which feedback is collected.
 * @param onFeedbackGiven A callback function to be called when feedback is given.
 * @param attributes Optional extra per-instance context (e.g. which category
 *   of a multi-category feature this feedback is about) merged into the
 *   feedback events and the Jira submission's context field.
 * @returns An object containing methods for showing or hiding the collector, determining if it is currently open, and
 * retrieving analytics metadata for the provided feature.
 * @example
 * ```ts
 * const {
 *   showFeedbackCollector,
 *   getAnalyticsAttributes,
 *   negativeFacets
 * } = useFeedbackCollector({ featureName: 'smartScheduling' });
 * ```
 */
export const useFeedbackCollector = ({
  featureName,
  onFeedbackGiven,
  attributes,
}: UseFeedbackCollectorProps): UseFeedbackCollectorReturn => {
  const feature = FeedbackFeatures[featureName];
  const intl = useIntl();
  /**
   * A method to get the attributes to be used in the attributes object of an analytics event that needs to send information to the feedback collector.
   * @param options - Object containing optional properties to modify the sentiment modifier
   * @param options.numNegativeFacets - The number of negative facets considered true for the feature based on the current flow. If not provided, the code will use a default value of 1.
   * @param options.fullyPositive - If true, the sentiment modifier will be set to 1.
   * @param options.fullyNegative - If true, the sentiment modifier will be set to -1.
   * @param options.numInBatch - The number of items in the batch. If provided, the sentiment modifier will be multiplied by this value before being returned.
   * @example
   * const { getAnalyticsAttributes } = useFeedbackCollector({ featureName: 'smartScheduling' });
   * const attributes = getAnalyticsAttributes({ numNegativeFacets: 3, numInBatch: 10 });
   * @returns Analytics attributes to be used in the attributes object of an analytics event
   */
  const getAnalyticsAttributes = (
    options?: GetAnalyticsAttributesOptions,
  ): Record<string, number | string> => {
    const multiplier = options?.numInBatch ?? 1;

    if (options?.fullyPositive) {
      return {
        aiLabsFeatureName: featureName,
        aiLabsSentimentModifier: multiplier,
      };
    }

    if (options?.fullyNegative) {
      return {
        aiLabsFeatureName: featureName,
        aiLabsSentimentModifier: -multiplier,
      };
    }

    return {
      aiLabsFeatureName: featureName,
      aiLabsSentimentModifier:
        getSentimentModifier(featureName, intl, options?.numNegativeFacets) *
        multiplier,
    };
  };

  const negativeFacets = feature.negativeFacets(intl);

  /**
   * A method to get the project key to be used in the feedback collector for freeform feedback, to properly track the feedback for the feature.
   * @returns The project key for the feature.
   */
  const getFeedbackCollectorProjectKey = (): string => {
    return 'project-key: ' + feature.projectKey();
  };

  const showFeedbackCollector = useCallback(
    ({
      traceId,
      flagTreatment,
      attributes: callSiteAttributes,
    }: ShowFeedbackCollectorOptions) => {
      feedbackCollectorSharedState.setValue({
        isOpen: true,
        traceId,
        featureName,
        flagTreatment,
        onFeedbackGiven,
        attributes:
          attributes || callSiteAttributes
            ? { ...attributes, ...callSiteAttributes }
            : undefined,
      });
    },
    [featureName, onFeedbackGiven, attributes],
  );

  const dismissFeedbackCollector = useCallback(() => {
    feedbackCollectorSharedState.setValue({ isOpen: false });
  }, []);

  const isOpen = useSharedStateSelector(
    feedbackCollectorSharedState,
    (state) => state.isOpen,
  );

  const traceId = useSharedStateSelector(
    feedbackCollectorSharedState,
    (state) => state.traceId,
  );

  return {
    getAnalyticsAttributes,
    negativeFacets,
    getFeedbackCollectorProjectKey,
    isOpen,
    traceId,
    showFeedbackCollector,
    dismissFeedbackCollector,
  };
};
